import { useState } from "react";
import { Box, CssBaseline, Divider, IconButton, List, ThemeProvider, Tooltip, Typography } from "@mui/material";

import {
    Activity,
    ArrowLeft,
    BarChart3,
    Box as BoxIcon,
    Check,
    CheckSquare,
    Clock,
    Download,
    FileSearch,
    FileStack,
    FileText,
    Gavel,
    LayoutGrid,
    ListChecks,
    MessageSquare,
    PanelLeftClose,
    PanelLeftOpen,
    Settings,
    Star,
    Target,
    User,
} from "lucide-react";

import { useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { DialogsProvider } from "@toolpad/core/useDialogs";

import { RootState } from "@store/store";
import { bpmV2Theme } from "@themes/Bpm";
import { AppButton } from "@components/v2/AppButton";
import { ModeToggle } from "@components/v2/ModeToggle";
import { TopbarTitle } from "@components/v2/TopbarTitle";
import { TopbarProfile } from "@components/v2/TopbarProfile";
import { DialogManager } from "@components/v2/DialogManager";
import { SidebarGroupLabel, SidebarNavItem } from "@components/v2/SidebarNavItem";
import { useTokenExpirationGuard } from "@hooks/useTokenExpirationGuard";

interface SidebarItemConfig {
    label: string;
    icon: React.ReactNode;
    /** V1 route segment under `/solicitud/:companyName/:id/<segment>` — undefined = no page yet. */
    segment?: string;
    /** Segment under `/v2/solicitud/:companyName/:id/<segment>` once a screen has its own v2 page — takes priority over `segment`. Empty string = the v2 index route. */
    v2Segment?: string;
}

interface SidebarGroupConfig {
    label: string;
    items: SidebarItemConfig[];
}

const SIDEBAR_GROUPS: SidebarGroupConfig[] = [
    {
        label: "Solicitud",
        items: [
            { label: "Solicitud", icon: <FileText size={16} />, v2Segment: "" },
            { label: "Perfil Financiero", icon: <Activity size={16} />, segment: "perfil_financiero", v2Segment: "perfil_financiero" },
            { label: "Perfil Identidad", icon: <User size={16} />, segment: "perfil_persona", v2Segment: "perfil_persona" },
            { label: "Perfil PLD", icon: <Gavel size={16} />, segment: "perfil_legal", v2Segment: "perfil_legal" },
            { label: "Oferta", icon: <Star size={16} />, segment: "oferta", v2Segment: "oferta" },
        ],
    },
    {
        label: "Evaluación",
        items: [
            { label: "Dictamen Legal", icon: <ListChecks size={16} />, segment: "dictamen_legal", v2Segment: "dictamen_legal" },
            { label: "Expediente", icon: <FileSearch size={16} />, segment: "expediente", v2Segment: "expediente" },
            { label: "Crédito e Identidad", icon: <Check size={16} />, segment: "riesgo_identidad" },
            { label: "PLD Integral", icon: <Clock size={16} />, segment: "pld_integral" },
            { label: "Visita Ocular", icon: <Target size={16} />, segment: "visita_integral", v2Segment: "visita_integral" },
        ],
    },
    {
        label: "Operación",
        items: [
            { label: "Revisión Control", icon: <CheckSquare size={16} />, segment: "revision_control", v2Segment: "revision_control" },
            { label: "Contractuales", icon: <FileStack size={16} />, segment: "contractuales" },
            { label: "Cartera y Dispersión", icon: <BoxIcon size={16} />, segment: "cartera_dispersion", v2Segment: "cartera_dispersion" },
            { label: "Resumen Operación", icon: <CheckSquare size={16} />, segment: "resumen" },
            { label: "Contratos", icon: <LayoutGrid size={16} /> },
        ],
    },
];

/**
 * Full BPM v2 shell (topbar + sidebar), built from `originacion.html`/
 * `originacion-pf.html`. Sidebar items with a `v2Segment` route within this
 * shell; the rest still navigate out to the existing (legacy-themed) routes
 * under `/solicitud/:companyName/:id/...` since those screens haven't been
 * migrated yet.
 */
export const SolicitudLayoutV2 = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    const [collapsed, setCollapsed] = useState(false);

    useTokenExpirationGuard();

    const userSession = useSelector((state: RootState) => state.session.user);
    const applicationSelected = useSelector((state: RootState) => state.components.applicationSelected);

    const initials = `${userSession.name?.charAt(0) ?? ""}${userSession.last_name?.charAt(0) ?? ""}`.toUpperCase() || "??";
    const folio = String(applicationSelected.id_application_company ?? "").padStart(5, "0");

    const v2Base = `/v2/solicitud/${params.companyName}/${params.id}`;
    const isItemActive = (item: SidebarItemConfig) =>
        item.v2Segment !== undefined && location.pathname === `${v2Base}/${item.v2Segment}`.replace(/\/$/, "");

    const goToScreen = (item: SidebarItemConfig) => {
        if (item.v2Segment !== undefined) {
            navigate(`${v2Base}/${item.v2Segment}`.replace(/\/$/, ""));
        } else if (item.segment) {
            navigate(`/solicitud/${params.companyName}/${params.id}/${item.segment}`);
        }
    };

    return (
        <ThemeProvider theme={bpmV2Theme}>
            <CssBaseline />
            <DialogsProvider>
                <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
                    {/* Topbar */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2.5,
                            flexShrink: 0,
                            height: 64,
                            px: 3,
                            backgroundColor: "background.paper",
                            borderBottom: "1px solid",
                            borderColor: "divider",
                        }}
                    >
                        <Tooltip title="Volver a la bandeja de solicitudes">
                            <IconButton onClick={() => navigate("/v2/solicitudes")} sx={{ color: "primary.main" }}>
                                <ArrowLeft size={18} />
                            </IconButton>
                        </Tooltip>
                        <Typography sx={{ typography: "h5", color: "text.primary", flexShrink: 0 }}>Lendia</Typography>
                        <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />
                        <TopbarTitle
                            ident={applicationSelected.company_rfc ?? "—"}
                            title={applicationSelected.company_name ?? applicationSelected.user_name ?? ""}
                        />
                        <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 2 }}>
                            <ModeToggle
                                options={[
                                    { value: "solicitud", label: "Solicitud", icon: <FileText size={13} /> },
                                    { value: "cliente", label: "Cliente", icon: <User size={13} /> },
                                ]}
                                value="solicitud"
                                onChange={() => {}}
                            />
                            <Divider orientation="vertical" flexItem sx={{ my: 1.5 }} />
                            <AppButton tone="ghost" startIcon={<Download size={14} />} sx={{ height: 34, fontSize: "0.75rem" }}>
                                Descargar
                            </AppButton>
                            <AppButton tone="ghost" startIcon={<MessageSquare size={14} />} sx={{ height: 34, fontSize: "0.75rem" }}>
                                Comentarios
                            </AppButton>
                            <AppButton
                                tone="ghost"
                                startIcon={<BarChart3 size={14} />}
                                sx={{ height: 34, fontSize: "0.75rem" }}
                                onClick={() => navigate("/reportes")}
                            >
                                Reportes
                            </AppButton>
                            <IconButton sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, width: 34, height: 34 }}>
                                <Settings size={14} />
                            </IconButton>
                            <TopbarProfile initials={initials} />
                        </Box>
                    </Box>

                    <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
                        {/* Sidebar */}
                        <Box
                            sx={{
                                width: collapsed ? 64 : 240,
                                flexShrink: 0,
                                display: "flex",
                                flexDirection: "column",
                                backgroundColor: "background.paper",
                                borderRight: "1px solid",
                                borderColor: "divider",
                                overflowY: "auto",
                                transition: "width 140ms ease",
                                py: 1,
                            }}
                        >
                            {!collapsed && (
                                <Box sx={{ px: 2.5, pb: 1 }}>
                                    <Typography sx={{ typography: "overline", fontSize: "0.625rem", color: "text.disabled" }}>
                                        Solicitud
                                    </Typography>
                                    <Box>
                                        <Typography
                                            component="span"
                                            sx={{
                                                display: "inline-block",
                                                mt: 0.5,
                                                fontFamily: "monospace",
                                                fontSize: "0.75rem",
                                                fontWeight: 700,
                                                color: "success.dark",
                                                backgroundColor: "success.light",
                                                px: 1.25,
                                                py: 0.5,
                                                borderRadius: 999,
                                            }}
                                        >
                                            {folio}
                                        </Typography>
                                    </Box>
                                </Box>
                            )}

                            {SIDEBAR_GROUPS.map((group) => (
                                <Box key={group.label}>
                                    {!collapsed && <SidebarGroupLabel>{group.label}</SidebarGroupLabel>}
                                    <List disablePadding>
                                        {group.items.map((item) => (
                                            <SidebarNavItem
                                                key={item.label}
                                                icon={item.icon}
                                                label={collapsed ? "" : item.label}
                                                selected={isItemActive(item)}
                                                onClick={() => goToScreen(item)}
                                            />
                                        ))}
                                    </List>
                                </Box>
                            ))}

                            <Box sx={{ mt: "auto", px: collapsed ? 0 : 2, pt: 1.5, display: "flex", justifyContent: collapsed ? "center" : "flex-start" }}>
                                <Tooltip title={collapsed ? "Expandir menú" : "Colapsar menú"}>
                                    <IconButton
                                        onClick={() => setCollapsed((prev) => !prev)}
                                        sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, gap: 1, px: collapsed ? 1 : 1.5, height: 32 }}
                                    >
                                        {collapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
                                        {!collapsed && (
                                            <Typography sx={{ typography: "button", fontSize: "0.6875rem" }}>Colapsar</Typography>
                                        )}
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>

                        {/* Main content */}
                        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", backgroundColor: "background.default" }}>
                            <Box sx={{ maxWidth: 1480, mx: "auto", px: 5, py: 3 }}>
                                <Outlet />
                            </Box>
                        </Box>
                    </Box>
                </Box>
                <DialogManager />
            </DialogsProvider>
        </ThemeProvider>
    );
};
